import { useState, useCallback, useEffect } from 'react';
import {
  GranularityLevelEnum,
  HistoryEventDto,
  HistoryEventGroupDto
} from 'api/activity-feed';
import { activityFeedAPI } from 'services/swagger-api';
import { getGroupContractHistory } from '../api/getGroupContractHistory';

interface UseActivityTimelineOptions {
  riskId?: string;
  borrowerProjectId?: string;
  groupContractId?: string;
  initialGranularity?: GranularityLevelEnum;
  pageSize?: number;
}

interface UseActivityTimelineReturn {
  events: HistoryEventDto[];
  groups: HistoryEventGroupDto[];
  granularity: GranularityLevelEnum;
  setGranularity: (level: GranularityLevelEnum) => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useActivityTimeline = (
  options: UseActivityTimelineOptions
): UseActivityTimelineReturn => {
  const {
    riskId,
    borrowerProjectId,
    groupContractId,
    initialGranularity = GranularityLevelEnum.Standard,
    pageSize = 20
  } = options;

  const [granularity, setGranularityState] =
    useState<GranularityLevelEnum>(initialGranularity);
  const [events, setEvents] = useState<HistoryEventDto[]>([]);
  const [groups, setGroups] = useState<HistoryEventGroupDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const useBorrowerProjectApi = !!borrowerProjectId;
  const useGroupContractApi = !!groupContractId;

  const fetchHistory = useCallback(
    async (page: number = 1, append: boolean = false) => {
      if (!riskId && !borrowerProjectId && !groupContractId) return;

      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      try {
        const command = {
          granularity,
          pageNumber: page,
          pageSize
        };

        const response = useGroupContractApi
          ? await getGroupContractHistory(groupContractId!, command)
          : useBorrowerProjectApi
            ? await activityFeedAPI.getHistoryByBorrowerProjectAsync({
                borrowerProjectId: borrowerProjectId!,
                getHistoryByGranularityCommand: command
              })
            : await activityFeedAPI.getHistoryByGranularityAsync({
                risqueAssureId: riskId!,
                getHistoryByGranularityCommand: command
              });

        setTotalCount(response.totalCount);

        // Determine which data to use based on granularity level
        const isStandardLevel = granularity === GranularityLevelEnum.Standard;

        if (isStandardLevel) {
          // Standard level uses groups
          const newGroups = response.groups || [];
          if (append) {
            setGroups((prev) => [...prev, ...newGroups]);
          } else {
            setGroups(newGroups);
            setEvents([]);
          }
          const totalGroupsCount = append
            ? groups.length + newGroups.length
            : newGroups.length;
          setHasMore(totalGroupsCount < response.totalCount);
        } else {
          // Resume and Complete levels use events
          const newEvents = response.events || [];
          if (append) {
            setEvents((prev) => [...prev, ...newEvents]);
          } else {
            setEvents(newEvents);
            setGroups([]);
          }
          const totalEventsCount = append
            ? events.length + newEvents.length
            : newEvents.length;
          setHasMore(totalEventsCount < response.totalCount);
        }
      } catch (err) {
        console.error('Error fetching activity timeline:', err);
        setError('Erreur lors du chargement de la timeline');
        if (!append) {
          setEvents([]);
          setGroups([]);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [riskId, borrowerProjectId, groupContractId, useBorrowerProjectApi, useGroupContractApi, granularity, pageSize, events.length, groups.length]
  );

  const setGranularity = useCallback((level: GranularityLevelEnum) => {
    setGranularityState(level);
    setCurrentPage(1);
    setHasMore(true);
  }, []);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await fetchHistory(nextPage, true);
  }, [currentPage, fetchHistory, hasMore, isLoadingMore]);

  const refresh = useCallback(async () => {
    setCurrentPage(1);
    setHasMore(true);
    await fetchHistory(1, false);
  }, [fetchHistory]);

  // Fetch on mount and when granularity changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchHistory(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskId, borrowerProjectId, groupContractId, granularity]);

  return {
    events,
    groups,
    granularity,
    setGranularity,
    isLoading,
    isLoadingMore,
    error,
    totalCount,
    hasMore,
    loadMore,
    refresh
  };
};
